import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Audit Logs
export const getAuditLogs = createAsyncThunk(
  "auditLogs/getAuditLogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.user_id) queryParams.append("user_id", params.user_id);
      if (params.model_type)
        queryParams.append("model_type", params.model_type);
      if (params.model_id) queryParams.append("model_id", params.model_id);
      if (params.event) queryParams.append("event", params.event);
      if (params.from_date) queryParams.append("from_date", params.from_date);
      if (params.to_date) queryParams.append("to_date", params.to_date);

      const url = `${BASE_URL}/audit-logs${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get audit logs!";
      return rejectWithValue(message);
    }
  },
);

// Get Audit Log Details
export const getAuditLogDetails = createAsyncThunk(
  "auditLogs/getAuditLogDetails",
  async ({ id, language }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = language || getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/audit-logs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get audit log details!";
      return rejectWithValue(message);
    }
  },
);

// Get Model Audit Logs
export const getModelAuditLogs = createAsyncThunk(
  "auditLogs/getModelAuditLogs",
  async ({ modelType, modelId, language }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = language || getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(
        `${BASE_URL}/audit-logs/model/${modelType}/${modelId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get model audit logs!";
      return rejectWithValue(message);
    }
  },
);

// Get Audit Log Statistics
export const getAuditLogStatistics = createAsyncThunk(
  "auditLogs/getAuditLogStatistics",
  async (language, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = language || getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/audit-logs/statistics`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get audit log statistics!";
      return rejectWithValue(message);
    }
  },
);

// Compare Audit Logs
export const compareAuditLogs = createAsyncThunk(
  "auditLogs/compareAuditLogs",
  async ({ logId1, logId2, language }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = language || getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${BASE_URL}/audit-logs/compare`,
        {
          log_id_1: logId1,
          log_id_2: logId2,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to compare audit logs!";
      return rejectWithValue(message);
    }
  },
);

// Cleanup Audit Logs
export const cleanupAuditLogs = createAsyncThunk(
  "auditLogs/cleanupAuditLogs",
  async ({ olderThanDays, language }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = language || getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(
        `${BASE_URL}/audit-logs/cleanup?older_than_days=${olderThanDays}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cleanup audit logs!";
      return rejectWithValue(message);
    }
  },
);

// Export Audit Logs to Excel
export const exportAuditLogsToExcel = async (params = {}) => {
  try {
    const token = getToken();
    const currentLang = getCurrentLanguage();
    if (!token) throw new Error("No authentication token found");
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append("user_id", params.user_id);
    if (params.model_type) queryParams.append("model_type", params.model_type);
    if (params.model_id) queryParams.append("model_id", params.model_id);
    if (params.event) queryParams.append("event", params.event);
    if (params.from_date) queryParams.append("from_date", params.from_date);
    if (params.to_date) queryParams.append("to_date", params.to_date);

    const url = `${BASE_URL}/audit-logs/export/excel${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      },
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to export audit logs!",
    );
  }
};

// Export Audit Logs to PDF
export const exportAuditLogsToPDF = async (params = {}) => {
  try {
    const token = getToken();
    const currentLang = getCurrentLanguage();
    if (!token) throw new Error("No authentication token found");
    const queryParams = new URLSearchParams();
    if (params.user_id) queryParams.append("user_id", params.user_id);
    if (params.model_type) queryParams.append("model_type", params.model_type);
    if (params.model_id) queryParams.append("model_id", params.model_id);
    if (params.event) queryParams.append("event", params.event);
    if (params.from_date) queryParams.append("from_date", params.from_date);
    if (params.to_date) queryParams.append("to_date", params.to_date);

    const url = `${BASE_URL}/audit-logs/export/pdf${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      },
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to export audit logs!",
    );
  }
};
